import { useState } from 'react';
import { useTranslations } from '~/services/umbraco/hooks';
import useAccountStatement from '~/services/shop/hooks/mit-fog/useAccountStatement';
import { DatePickerRange } from '~/shared/components/DatePicker';
import SearchField from '~/shared/components/SearchField';
import { SkeletonLoader } from '~/shared/components/SkeletonLoader';
import { Text } from '~/shared/components/Text';
import { ModuleContainer } from '../../shared/ModuleContainer';
import StatementTable from './components/Table';
import { useStatementFilterData } from './hooks/useStatementFilterData';
import {
    Footer,
    PageSetter,
    Pagination,
    Section,
    SkeletonSpacer,
    StyledFilterWrapper,
    StyledSearchWrapper,
} from './styles';

export function M203AccountStatement() {
    const [dateRange, setDateRange] = useState<(Date | null)[]>([null, null]);

    const { data, isLoading } = useAccountStatement();

    const { query, setQuery, filteredData } = useStatementFilterData(data, dateRange);

    const { translate } = useTranslations();

    if (isLoading || !data) return <Skeleton />;

    return (
        <ModuleContainer spacingTop="none">
            <Section>
                <Text as="h2" variant="display2">
                    {translate('mit-fog.accountStatement.accountStatement')} ({filteredData?.length}
                    )
                </Text>
                <StyledFilterWrapper>
                    <StyledSearchWrapper>
                        <SearchField
                            type="text"
                            value={query}
                            setInputValue={setQuery}
                            placeholder={translate('mit-fog.accountStatement.placeholder')}
                        />
                    </StyledSearchWrapper>

                    <DatePickerRange
                        onChange={(dates) => (dates ? setDateRange(dates) : null)}
                        selectsRange
                        setDateRange={setDateRange}
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        maxDate={new Date()}
                        minDate={new Date(new Date().getFullYear() - 3, 0, 1)}
                    />
                </StyledFilterWrapper>
                <StatementTable data={filteredData} />
            </Section>
        </ModuleContainer>
    );
}

export default M203AccountStatement;

function Skeleton() {
    return (
        <ModuleContainer spacingTop="none">
            <Section>
                <SkeletonLoader width={300} height={38} />

                <StyledFilterWrapper>
                    <SkeletonLoader height={40} width={215} />

                    <SkeletonLoader height={40} width={210} />
                </StyledFilterWrapper>
                <div>
                    {Array.from({ length: 11 }).map((_, index) => (
                        <div key={index}>
                            <SkeletonLoader isFullWidth height={48} />
                            <SkeletonSpacer />
                        </div>
                    ))}
                </div>
                <Footer>
                    <PageSetter>
                        <SkeletonLoader height={40} width={150} />
                    </PageSetter>
                    <Pagination>
                        <SkeletonLoader height={40} width={40} />
                        <SkeletonLoader height={40} width={150} />
                        <SkeletonLoader height={40} width={40} />
                    </Pagination>
                </Footer>
            </Section>
        </ModuleContainer>
    );
}
